/*!
 * @license
 * Copyright Squiz Australia Pty Ltd. All Rights Reserved.
 */

import { useObservable } from '@squiz/dxp-console-support';
import { useMemo } from 'react';
import { Observable } from 'rxjs';
import { JsonObject } from 'type-fest';
import { EntityJobContext } from '../../Entities';
import { getJobRunnerService } from '../../Utilities/GetJobRunnerService/GetJobRunnerService';

/**
 * The result expected of the useContexts hook.
 */
export type JobContextsResult = [Array<EntityJobContext> | null, JsonObject | null];

/**
 * React hook to get a list of job contexts for this tenant.
 */
export function useJobContexts(cacheKey = `1`): JobContextsResult {
  // Use of cacheKey as a dependency enables useMemo and useObservable to update if the value changes
  const jobRunnerService = getJobRunnerService();

  const jobContexts$ = useMemo((): Observable<Array<EntityJobContext> | null> => {
    return jobRunnerService.getJobContexts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cacheKey, jobRunnerService]);

  const [jobContexts, error] = useObservable(jobContexts$, [cacheKey, jobRunnerService]);

  return useMemo((): JobContextsResult => {
    return [jobContexts as Array<EntityJobContext> | null, error as JsonObject | null];
  }, [error, jobContexts]);
}
